import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/common/SEO";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { SOCIAL_ICONS as ICONS } from "@/components/common/SocialIcons";
import { api, formatApiError } from "@/lib/api";
import { brand, social } from "@/config/brand";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address";
    if (!form.message.trim()) e.message = "Please enter a message";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-border bg-transparent px-4 py-3 text-sm focus:border-gold focus:outline-none";

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <SEO title="Contact Us" description={`Get in touch with ${brand.name}. We're here to help with orders, custom pieces and anything in between.`} />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />

      <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20">
        {/* Info */}
        <div>
          <span className="text-luxe text-xs font-medium text-gold">We'd Love To Hear From You</span>
          <h1 className="mt-4 font-serif text-4xl font-light text-ink sm:text-5xl">Get In Touch</h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
            Have a question about an order, a bespoke commission or just want to say hello? Our team responds within one business day.
          </p>

          <ul className="mt-8 space-y-4 text-sm">
            <li className="flex items-center gap-4">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-gold-light text-gold"><Mail size={17} /></span>
              <a href={`mailto:${brand.email}`} className="text-ink hover:text-gold">{brand.email}</a>
            </li>
            <li className="flex items-center gap-4">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-gold-light text-gold"><Phone size={17} /></span>
              <a href={`tel:${brand.phone}`} className="text-ink hover:text-gold">{brand.phone}</a>
            </li>
            <li className="flex items-center gap-4">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-gold-light text-gold"><MapPin size={17} /></span>
              <span className="text-ink">{brand.address}</span>
            </li>
          </ul>

          <div className="mt-8">
            <p className="text-luxe text-[10px] text-ink">Follow Us</p>
            <div className="mt-3 flex gap-3">
              {social.map((s) => {
                const Icon = ICONS[s.icon] || Instagram;
                return (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="grid h-10 w-10 place-items-center rounded-full border border-border text-ink transition-colors hover:border-gold hover:text-gold">
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="mt-10 border border-border bg-cream p-6">
            <p className="font-serif text-xl text-ink">Looking for quick answers?</p>
            <p className="mt-1 text-sm text-muted-foreground">Browse our FAQ for shipping, returns and care.</p>
            <Link to="/faq" className="mt-4 inline-block text-xs uppercase tracking-widest text-ink underline-offset-4 hover:text-gold hover:underline">
              Visit the FAQ →
            </Link>
          </div>
        </div>

        {/* Form */}
        <div className="border border-border bg-white/60 p-6 sm:p-8">
          {sent ? (
            <div className="flex h-full flex-col items-center justify-center py-16 text-center">
              <CheckCircle2 className="h-12 w-12 text-gold" strokeWidth={1.3} />
              <p className="mt-5 font-serif text-2xl text-ink">Thank you!</p>
              <p className="mt-2 text-sm text-muted-foreground">Your message has been received. We'll reply shortly.</p>
              <button onClick={() => setSent(false)} className="mt-6 text-xs uppercase tracking-widest text-ink hover:text-gold">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" noValidate data-testid="contact-form">
              <div>
                <label className="text-luxe text-[10px] text-ink">Name *</label>
                <input value={form.name} onChange={(e) => set("name", e.target.value)} className={`mt-2 ${inputCls}`} data-testid="contact-name-input" />
                {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="text-luxe text-[10px] text-ink">Email *</label>
                  <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={`mt-2 ${inputCls}`} data-testid="contact-email-input" />
                  {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
                </div>
                <div>
                  <label className="text-luxe text-[10px] text-ink">Phone</label>
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={`mt-2 ${inputCls}`} data-testid="contact-phone-input" />
                </div>
              </div>
              <div>
                <label className="text-luxe text-[10px] text-ink">Message *</label>
                <textarea rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className={`mt-2 resize-none ${inputCls}`} data-testid="contact-message-input" />
                {errors.message && <p className="mt-1 text-xs text-destructive">{errors.message}</p>}
              </div>
              <button type="submit" disabled={busy} data-testid="contact-submit-button" className="w-full bg-ink py-4 text-[11px] font-medium uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-gold disabled:opacity-60">
                {busy ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
